import React, { useEffect, useState } from 'react';
import { Chip, Container, Divider, Stack, Typography } from '@mui/material';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Unstable_Grid2';
import { useSnackbar } from 'notistack';
import { useLocation, useNavigate } from 'react-router-dom';

import InputForm from '../../../components/forms/InputForm';
import SelectPicker from '../../../components/forms/SelectPicker';
import DatePicker from '../../../components/datePickers/DatePicker';
import { useTranslation } from 'react-i18next';
import TransactionItemModal from './components/TransactionItemModal';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { defaultAccountCodes } from '../../../constants/defaultCodes/accountDefaultCodes';
import voucherTypes from '../../../constants/defaultCodes/voucherTypes';
import {
  getVoucherNumber,
  uploadVoucherImage,
} from '../../../controllers/transactions/transactionControllers';
import {
  accountByDefCode,
  bankAndCashAccounts,
} from '../../../controllers/settings/accountControllers';
import { getCustomers } from '../../../controllers/settings/partnerControllers';
import {
  getProductItems,
  getProductUnits,
} from '../../../controllers/settings/inventoryControllers';
import { getCustomerBalance } from '../../../controllers/reports/partnerReports';
import { getRefNoFromVoucherNo } from '../../../shared/utils/voucherUtils';
import { salesReturn } from '../../../controllers/transactions/incomeControllers';
import TransactionItem from './components/TransactionItem';
import AddNewLine from './components/AddNewLine';
import Button from '../../../components/buttons/Button';
import CashOrCreditSwitch from '../components/CashOrCreditSwitch';
import ItemListHeader from '../components/ItemListHeader';
import TransactionError from './components/TransactionError';
import BillOrInvoiceSummary from '../components/BillOrInvoiceSummary';
import { errorToast, successToast } from '../../../constants/snackbar/snackbar';
import {
  validateVoucherNo,
  validateSalesReturn,
} from '../../../validations/transactions/validateSalesReturn';
import ImagePicker from '../../../components/image/ImagePicker';

const { INCOME_FROM_SALES } = defaultAccountCodes;

const voucherType = voucherTypes.SALES_RETURN;
const initial = { initialData: [] };
const cache = { cacheTime: 0 };

const SalesReturn = ({ navigation, route }: any): JSX.Element => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const now = new Date();
  const initState = { voucherDate: now, voucherItems: [], isFullPayment: true };
  const [formData, setData] = useState<any>({ ...initState });
  const [images, setImages] = useState<any>([]);
  const [errors, setErrors] = useState<any>({});
  const [showItemModal, setShowItemModal] = useState<boolean>(false);
  const [activeLineNo, setActiveLineNo] = useState<number>(1);
  const [customerReceivable, setCustomerReceivable] = useState<any>();

  const { isFullPayment, paidAmount } = formData;
  // const returnVoucherId = route.params?.voucherId;

  const voucherNoFn = () => getVoucherNumber(voucherType);
  const queryVNO = useQuery(['getVoucherNo', voucherType], voucherNoFn);
  const { data: voucherNumber } = queryVNO;

  const customers = useQuery(['getCustomers'], getCustomers, initial);
  const bankAndCash = useQuery(['bankAndCash'], bankAndCashAccounts, initial);
  const products = useQuery(['products'], getProductItems, initial);
  const productUnits = useQuery(['productUnits'], getProductUnits, initial);
  const customerBalanceQuery = useMutation(getCustomerBalance);

  const accountByCodeFn = () => accountByDefCode(INCOME_FROM_SALES);
  const { data: defAccount } = useQuery(['incomeAccount'], accountByCodeFn);

  useEffect(() => {
    if (voucherNumber) {
      const referenceId = getRefNoFromVoucherNo(voucherNumber);
      setData((prev: any) => ({ ...prev, voucherNumber, referenceId }));
    }
  }, [voucherNumber]);

  const { isLoading, mutateAsync } = useMutation(salesReturn);
  const saveImage = useMutation(uploadVoucherImage);

  const onSubmit = async () => {
    if (!isFullPayment && paidAmount <= 0 && formData.cashOutAccount) {
      delete formData.cashOutAccount;
    }

    const { isValid, errs } = validateSalesReturn(formData, errors);
    const { isVoucherNoValid, errss } = await validateVoucherNo(formData, errs);

    if (isValid && isVoucherNoValid) {
      try {
        if (images && images.length) {
          const imgPaylaod = { images, voucherType: 'sales-return' };
          const image = await saveImage.mutateAsync(imgPaylaod);
          formData.image = image.id;
        }

        await mutateAsync(formData);
        queryClient.invalidateQueries(['getDashboardReport']);
        enqueueSnackbar(t('salesReturn.createMsg.success'), successToast);
        navigate('/journals', { replace: true });
      } catch (err) {
        // console.error('err:', err);
        let title = t('salesReturn.createMsg.error');

        if (err.response?.data?.statusCode === 400) {
          title = err.response?.data?.message;
        }
        enqueueSnackbar(title, errorToast);
      }
    } else {
      setErrors(errss);
      if (!isVoucherNoValid) {
        queryClient.invalidateQueries(['getVoucherNo']);
      }
    }
  };

  const onFormChange = async (name: string, value: any) => {
    setData((prev: any) => ({ ...prev, [name]: value }));

    const updatedData = { ...formData, [name]: value };
    const { errs } = validateSalesReturn(updatedData, errors);
    errs[name] ? setErrors({ [name]: errs[name] }) : setErrors({});

    if (name === 'customer' && value && formData.customer !== value) {
      const response = await customerBalanceQuery.mutateAsync(value);
      setCustomerReceivable({ type: 'CASH_IN', balance: response });
    } else if (name === 'customer' && !value) {
      setCustomerReceivable(null);
    }
  };

  const onFormItemValueChange = (data: any, lineNo: number) => {
    const myItem = formData.voucherItems.find(item => item.lineNo === lineNo);

    if (!myItem) {
      const vItems = [...formData.voucherItems, { ...data, lineNo }];
      setData(p => ({ ...p, voucherItems: vItems }));
      return;
    }

    const voucherItems = formData.voucherItems.map(item => {
      item.lineNo === lineNo && (item = { ...myItem, ...data });
      return item;
    });

    setData(prev => ({ ...prev, voucherItems }));
  };

  const onAddNewLine = () => {
    let newLine = formData.voucherItems.length + 1;

    let loopBreaker = 0;
    while (true) {
      const lineNo = formData.voucherItems.find(i => i.lineNo === newLine);
      if (!lineNo) {
        break;
      }
      newLine += 1;
      loopBreaker += 1;
      if (loopBreaker >= 100) {
        break;
      }
    }
    setShowItemModal(true);
    setActiveLineNo(newLine);
  };

  const onRemoveLine = (lineNo: number) => {
    if (formData.voucherItems.length <= 1) {
      return;
    }

    const voucherItems = formData.voucherItems.filter(
      (item: any) => item.lineNo !== lineNo,
    );

    setData(p => ({ ...p, voucherItems: [...voucherItems] }));
  };

  const getHelpText = (fieldName: string) => {
    if (fieldName in errors) {
      return errors[fieldName];
    }
  };

  // console.log('formData invoice:', formData);
  // console.log('errors invoice:', errors);
  // console.log('--- --- --- ---');
  const activeItem = formData.voucherItems.find(x => x.lineNo === activeLineNo);

  return (
    <Container>
      <Card sx={{ paddingX: 2 }}>
        <CardContent>
          <Stack pb={1} direction={'row'} justifyContent={'space-between'}>
            <Typography sx={{ fontSize: 20 }} color="text.primary" gutterBottom>
              {t('transactionStack.salesReturn')}
            </Typography>

            <CashOrCreditSwitch
              isFullPayment={isFullPayment}
              onToggle={v => onFormChange('isFullPayment', v)}
            />
          </Stack>
          <Divider />

          <Stack py={2} gap={2}>
            <Grid container columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
              <Grid xs={4}>
                <DatePicker
                  entryMinDate={true}
                  label={t('salesReturn.label.voucherDate')}
                  onDateChange={(v: any) => onFormChange('voucherDate', v)}
                  value={formData.voucherDate}
                />
              </Grid>

              <Grid xs={4}>
                <InputForm
                  isDisabled={true}
                  isRequired={true}
                  label={t('salesReturn.label.voucherNumber')}
                  isInvalid={'voucherNumber' in errors}
                  helpText={getHelpText('voucherNumber')}
                  defaultValue={formData.voucherNumber}
                />
              </Grid>
              <Grid xs={4}>
                <InputForm
                  defaultValue={formData.referenceId}
                  label={t('salesReturn.label.referenceId')}
                  isInvalid={'referenceId' in errors}
                  helpText={'referenceId' in errors && errors.referenceId}
                  onChangeText={(v: string) => onFormChange('referenceId', v)}
                />
              </Grid>
            </Grid>
            <Grid container columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
              <Grid xs={12}>
                <SelectPicker
                  isRequired={isFullPayment ? false : true}
                  label={t('salesIncome.label.customer')}
                  selectedValue={formData.customer}
                  selectItems={customers.data}
                  isInvalid={'customer' in errors}
                  helpText={'customer' in errors && errors.customer}
                  onValueChange={(v: any) => onFormChange('customer', v)}
                  partnerBalance={customerReceivable && customerReceivable}
                />
              </Grid>
            </Grid>
            <Stack pt={1}>
              <ItemListHeader
                itemsLen={formData.voucherItems.length}
                title="Return Items"
              />

              {formData.voucherItems.map((item: any, index) => (
                <TransactionItem
                  key={item.lineNo}
                  index={index + 1}
                  voucherItem={item}
                  productItems={products.data}
                  productUnits={productUnits.data}
                  onFormValueChange={onFormItemValueChange}
                  onRemoveLine={onRemoveLine}
                  isSalesReturn
                />
              ))}
            </Stack>

            {showItemModal && (
              <TransactionItemModal
                showModal={showItemModal}
                setShowModal={setShowItemModal}
                voucherItem={activeItem || { lineNo: activeLineNo }}
                productItems={products.data || []}
                productUnits={productUnits.data || []}
                onFormValueChange={onFormItemValueChange}
                // setHandleProduct={setHandleProduct}
                defaultAccount={defAccount}
                isSalesReturn
              />
            )}

            <AddNewLine {...{ onAddNewLine, t }} />

            {errors.salesAmount && (
              <TransactionError error={errors.salesAmount} />
            )}

            {/* Not Handled the partial payments for return transaction */}
            {/* <PaymentAmount
              {...{ formData, onFormChange, errors, type: 'SALES', t }}
            /> */}

            {(paidAmount > 0 || isFullPayment) && (
              <SelectPicker
                isRequired={paidAmount || isFullPayment ? true : false}
                selectedValue={formData.cashOutAccount}
                selectItems={bankAndCash.data}
                label={t('salesReturn.label.cashOutAccount')}
                isInvalid={'cashOutAccount' in errors}
                helpText={'cashOutAccount' in errors && errors.cashOutAccount}
                onValueChange={(v: string) => onFormChange('cashOutAccount', v)}
              />
            )}

            <BillOrInvoiceSummary
              t={t}
              voucherItems={formData.voucherItems}
              discountAmount={formData.discountAmount}
              paidAmount={formData.paidAmount}
              isTaxable={formData.isTaxable}
              isReturn
            />

            <Stack flexDirection={'row'} gap={3} alignItems={'center'}>
              <Stack flex={1}>
                <InputForm
                  inLineLabel={true}
                  defaultValue={formData.description}
                  label={t('salesReturn.label.description')}
                  isInvalid={'description' in errors}
                  helpText={'description' in errors && errors.description}
                  onChangeText={(v: string) => onFormChange('description', v)}
                />
              </Stack>
              <Stack pt={0.1}>
                <ImagePicker {...{ images, setImages }} />
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
        <Divider />

        <Stack
          direction="row"
          justifyContent={'flex-end'}
          spacing={4}
          padding={2}>
          <Button
            title="Save Sales Return"
            onClick={onSubmit}
            loading={isLoading || saveImage.isLoading}
          />
        </Stack>
      </Card>
    </Container>
  );
};

export default SalesReturn;
